import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserCircleIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  EnvelopeIcon,
  UserIcon,
  ShieldCheckIcon,
  ClockIcon as ClockIconSolid,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { adminService, ActivityLog, User } from "../../services/admin";
import { getImageUrl } from "../../services/api";

// User Profile Modal Component
const UserProfileModal: React.FC<{
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ user, isOpen, onClose }) => {
  console.log("UserProfileModal props:", { user, isOpen });

  if (!isOpen || !user) return null;

  const getStatusBadge = (status: string) => {
    let colorClass = "bg-gray-100 text-gray-800";
    let text = "Unknown";

    switch (status) {
      case "approved":
        colorClass = "bg-green-100 text-green-800";
        text = "ອະນຸຍາດ";
        break;
      case "pending":
        colorClass = "bg-yellow-100 text-yellow-800";
        text = "ລໍຖ້າ";
        break;
      case "blocked":
        colorClass = "bg-red-100 text-red-800";
        text = "ຖືກບລັອກ";
        break;
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
      >
        {text}
      </span>
    );
  };

  const getRoleBadge = (role: string) => {
    const isAdmin = role === "admin";
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isAdmin
            ? "bg-purple-100 text-purple-800"
            : "bg-blue-100 text-blue-800"
        }`}
      >
        {isAdmin ? "ຜູ້ບໍລິຫານ" : "ນັກຮຽນ"}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("lo-LA", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              ຂໍ້ມູນຜູ້ໃຊ້
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="ປິດ"
              aria-label="ປິດ"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Profile Image */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {user.profile_image ? (
                <img
                  src={getImageUrl(user.profile_image)}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                  onLoad={() =>
                    console.log(
                      "Profile image loaded successfully:",
                      user.profile_image || "",
                    )
                  }
                  onError={(e) => {
                    console.error(
                      "Failed to load profile image:",
                      user.profile_image || "",
                    );
                    console.error(
                      "Attempted URL:",
                      getImageUrl(user.profile_image || ""),
                    );
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextElementSibling?.classList.remove(
                      "hidden",
                    );
                  }}
                />
              ) : null}
              <div
                className={`w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center ${user.profile_image ? "hidden" : ""}`}
              >
                <UserCircleIcon className="h-12 w-12 text-gray-400" />
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="space-y-4">
            {/* Name and Email */}
            <div className="text-center">
              <h4 className="text-xl font-semibold text-gray-900">
                {user.name}
              </h4>
              <div className="flex items-center justify-center mt-1 text-gray-600">
                <EnvelopeIcon className="h-4 w-4 mr-1" />
                {user.email}
              </div>
              <div className="flex items-center justify-center mt-1 text-gray-600 font-mono">
                <UserCircleIcon className="h-4 w-4 mr-1" />
                {user.student_id || `STD-${String(user.id).padStart(5, "0")}`}
              </div>
            </div>

            {/* Status and Role */}
            <div className="flex justify-center space-x-2">
              {getStatusBadge(user.status)}
              {getRoleBadge(user.role)}
            </div>

            {/* Personal Information */}
            <div className="space-y-3">
              {user.phone && (
                <div className="flex items-center text-sm">
                  <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-gray-700">{user.phone}</span>
                </div>
              )}

              {user.age && (
                <div className="flex items-center text-sm">
                  <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-gray-700">ອາຍຸ: {user.age} ປີ</span>
                </div>
              )}

              {user.date_of_birth && (
                <div className="flex items-center text-sm">
                  <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-gray-700">
                    ວັນເດືອນປີເກີດ: {formatDate(user.date_of_birth)}
                  </span>
                </div>
              )}

              {/* Location */}
              {(user.village || user.district || user.province) && (
                <div className="flex items-start text-sm">
                  <MapPinIcon className="h-4 w-4 mr-2 mt-0.5 text-gray-400" />
                  <div className="text-gray-700">
                    {[user.village, user.district, user.province]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </div>
              )}

              {/* Account Information */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center text-sm mb-2">
                  <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-gray-700">
                    ສ້າງບັນຊີ: {formatDate(user.created_at)}
                  </span>
                </div>

                {user.last_login && (
                  <div className="flex items-center text-sm">
                    <ClockIconSolid className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="text-gray-700">
                      ເຂົ້າສູ້ລະບົບຄັ້ງສຸດທ້າຍ: {formatDate(user.last_login)}
                    </span>
                  </div>
                )}

                {user.blocked_until && (
                  <div className="flex items-center text-sm text-red-600">
                    <ShieldCheckIcon className="h-4 w-4 mr-2" />
                    <span>
                      ຖືກບລັອກຈົນເຖິງ: {formatDate(user.blocked_until)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              ປິດ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ACTION_TYPES: Record<
  string,
  { method: string; endpoint: string; description: string }
> = {
  USER_LOGIN: {
    method: "POST",
    endpoint: "/api/auth/login",
    description: "ເຂົ້າສູ້ລະບົບ",
  },
  USER_REGISTER: {
    method: "POST",
    endpoint: "/api/auth/register",
    description: "ລົງທະບຽນຜູ້ໃຊ້",
  },
  USER_LOGOUT: {
    method: "POST",
    endpoint: "/api/auth/logout",
    description: "ອອກຈາກລະບົບ",
  },
  PROFILE_UPDATE: {
    method: "PUT",
    endpoint: "/api/users/profile",
    description: "ອັບເດດ ຮູບໂປໄຟຮ",
  },
  VIDEO_STREAM: {
    method: "GET",
    endpoint: "/api/videos/stream",
    description: "ເລີ່ມສະຖີບວິດີໂອ",
  },
  VIDEO_COMPLETED: {
    method: "POST",
    endpoint: "/api/videos/complete",
    description: "ສຳເລັດການຊົມວິດີໂອ",
  },
  VIDEO_UPLOAD: {
    method: "POST",
    endpoint: "/api/videos/upload",
    description: "ອັບໂຫລດວິດີໂອ",
  },
  VIDEO_DELETE: {
    method: "DELETE",
    endpoint: "/api/videos",
    description: "ລຶບວິດີໂອ",
  },
  CATEGORY_CREATE: {
    method: "POST",
    endpoint: "/api/categories",
    description: "ສ້າງໝວດໝູ່",
  },
  CATEGORY_UPDATE: {
    method: "PUT",
    endpoint: "/api/categories",
    description: "ອັບເດດໝວດໝູ່",
  },
  CATEGORY_DELETE: {
    method: "DELETE",
    endpoint: "/api/categories",
    description: "ລຶບໝວດໝູ່",
  },
  FOLDER_CREATE: {
    method: "POST",
    endpoint: "/api/folders",
    description: "ສ້າງໂຟລເດີ",
  },
  FOLDER_UPDATE: {
    method: "PUT",
    endpoint: "/api/folders",
    description: "ອັບເດດໂຟລເດີ",
  },
  FOLDER_DELETE: {
    method: "DELETE",
    endpoint: "/api/folders",
    description: "ລຶບໂຟລເດີ",
  },
  CERTIFICATE_GENERATE: {
    method: "POST",
    endpoint: "/api/certificates/generate",
    description: "ສ້າງໃບຮັບຮອງ",
  },
  CERTIFICATE_DOWNLOAD: {
    method: "GET",
    endpoint: "/api/certificates/download",
    description: "ດາວໂຫລດໃບຮັບຮອງ",
  },
  USER_APPROVE: {
    method: "PUT",
    endpoint: "/api/admin/users/approve",
    description: "ອະນຸຍາດຜູ້ໃຊ້",
  },
  USER_BLOCK: {
    method: "PUT",
    endpoint: "/api/admin/users/block",
    description: "ບລັອກຜູ້ໃຊ້",
  },
  USER_UNBLOCK: {
    method: "PUT",
    endpoint: "/api/admin/users/unblock",
    description: "ຍົກເລີກການບລັອກຜູ້ໃຊ້",
  },
  USER_DELETE: {
    method: "DELETE",
    endpoint: "/api/admin/users",
    description: "ລຶບຜູ້ໃຊ້",
  },
  SETTINGS_UPDATE: {
    method: "PUT",
    endpoint: "/api/admin/settings",
    description: "ອັບເດດການຕັ້ງຄ່າ",
  },
  ANALYTICS_VIEW: {
    method: "GET",
    endpoint: "/api/admin/analytics",
    description: "ຊົມຂໍ້ມູນການວິເຄາະ",
  },
  ACTIVITY_LOGS_VIEW: {
    method: "GET",
    endpoint: "/api/admin/activity-logs",
    description: "ຊົມບັນທຶກການເຄື່ອນໄຫວ",
  },
};

const ActivityLogs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const [actionFilter, setActionFilter] = useState(
    searchParams.get("action") || "",
  );
  const [userFilter, setUserFilter] = useState(
    searchParams.get("userId") || "",
  );
  const [startDate, setStartDate] = useState(
    searchParams.get("startDate") || "",
  );
  const [endDate, setEndDate] = useState(searchParams.get("endDate") || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page") || "1", 10),
  );
  const actions = Object.keys(ACTION_TYPES);

  // User profiles state for real-time display
  const [userProfiles, setUserProfiles] = useState<Record<number, User>>({});
  const [loadingUsers, setLoadingUsers] = useState<Set<number>>(new Set());
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // User profile modal state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    // Extract filters from URL
    const action = searchParams.get("action") || "";
    const userId = searchParams.get("userId") || "";
    const search = searchParams.get("search") || "";
    const start = searchParams.get("startDate") || "";
    const end = searchParams.get("endDate") || "";
    const page = parseInt(searchParams.get("page") || "1", 10);

    setActionFilter(action);
    setUserFilter(userId);
    setSearchTerm(search);
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(page);

    fetchLogs({
      action,
      userId,
      search,
      startDate: start,
      endDate: end,
      page,
    });
  }, [searchParams]);

  // Real-time updates every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log("Refreshing activity logs for real-time updates...");
      fetchLogs();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [currentPage, actionFilter, userFilter, searchTerm, startDate, endDate]);

  const fetchLogs = async (filters?: {
    page?: number;
    limit?: number;
    userId?: string;
    action?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      setIsLoading(true);
      const data = await adminService.getActivityLogs(
        filters?.page ?? currentPage,
        filters?.limit ?? pagination.limit,
        filters?.userId ?? userFilter,
        filters?.action ?? actionFilter,
        filters?.search ?? searchTerm,
        filters?.startDate ?? startDate,
        filters?.endDate ?? endDate,
      );

      setLogs(data.logs);
      setPagination(data.pagination);
      setLastUpdate(new Date());

      // Fetch user profiles for all logs
      await fetchUserProfiles(data.logs);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserProfiles = async (activityLogs: ActivityLog[]) => {
    const uniqueUserIds = Array.from(
      new Set(activityLogs.map((log) => log.user_id)),
    );

    for (const userId of uniqueUserIds) {
      // Skip if we already have this user's profile or if it's already loading
      if (userProfiles[userId] || loadingUsers.has(userId)) {
        continue;
      }

      try {
        setLoadingUsers((prev) => new Set(prev).add(userId));
        const userData = await adminService.getUser(userId);
        setUserProfiles((prev) => ({ ...prev, [userId]: userData }));
      } catch (error) {
        console.error(`Error fetching user ${userId}:`, error);
      } finally {
        setLoadingUsers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      }
    }
  };

  const handleUserClick = async (userId: number) => {
    try {
      console.log("=== handleUserClick called ===");
      console.log("User ID:", userId);

      console.log("Calling adminService.getUser...");
      const userData = await adminService.getUser(userId);
      console.log("Fetched user data:", userData);

      console.log("Setting selected user...");
      setSelectedUser(userData);

      console.log("Setting modal to open...");
      setShowUserModal(true);

      console.log("=== handleUserClick completed ===");
    } catch (error: any) {
      console.error("Error in handleUserClick:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      alert("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນຜູ້ໃຊ້ໄດ້");
    }
  };

  const closeUserModal = () => {
    console.log("Closing user modal");
    setShowUserModal(false);
    setSelectedUser(null);
  };

  // Debug state changes
  useEffect(() => {
    console.log("Modal state changed:", {
      showUserModal,
      selectedUser: selectedUser?.id,
    });
  }, [showUserModal, selectedUser]);

  const updateSearchParams = (params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);

    // Update params
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateSearchParams({
      search: searchTerm,
      action: actionFilter,
      userId: userFilter,
      startDate: startDate,
      endDate: endDate,
      page: "1",
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateSearchParams({ page: page.toString() });
  };

  const getActionInfo = (action: string, details: string) => {
    const actionInfo = ACTION_TYPES[action] || {
      method: "GET",
      endpoint: "/api/unknown",
      description: details || "ການກະທຳທີ່ບໍ່ຮູ້",
    };

    return actionInfo;
  };

  const getActionBadge = (action: string) => {
    let colorClass = "bg-gray-100 text-gray-800";

    if (
      action.includes("LOGIN") ||
      action.includes("REGISTER") ||
      action.includes("LOGOUT")
    ) {
      colorClass = "bg-blue-100 text-blue-800";
    } else if (
      action.includes("CREATE") ||
      action.includes("UPLOAD") ||
      action.includes("GENERATE")
    ) {
      colorClass = "bg-green-100 text-green-800";
    } else if (action.includes("DELETE") || action.includes("REMOVE")) {
      colorClass = "bg-red-100 text-red-800";
    } else if (
      action.includes("UPDATE") ||
      action.includes("EDIT") ||
      action.includes("CHANGE")
    ) {
      colorClass = "bg-yellow-100 text-yellow-800";
    } else if (
      action.includes("VIEW") ||
      action.includes("STREAM") ||
      action.includes("DOWNLOAD")
    ) {
      colorClass = "bg-indigo-100 text-indigo-800";
    } else if (action.includes("APPROVE") || action.includes("UNBLOCK")) {
      colorClass = "bg-emerald-100 text-emerald-800";
    } else if (action.includes("BLOCK")) {
      colorClass = "bg-orange-100 text-orange-800";
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
      >
        {action}
      </span>
    );
  };

  const getMethodBadge = (method: string) => {
    let colorClass = "bg-gray-100 text-gray-800";

    switch (method) {
      case "GET":
        colorClass = "bg-blue-100 text-blue-800";
        break;
      case "POST":
        colorClass = "bg-green-100 text-green-800";
        break;
      case "PUT":
        colorClass = "bg-yellow-100 text-yellow-800";
        break;
      case "DELETE":
        colorClass = "bg-red-100 text-red-800";
        break;
      case "PATCH":
        colorClass = "bg-purple-100 text-purple-800";
        break;
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono font-medium ${colorClass}`}
      >
        {method}
      </span>
    );
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  if (isLoading && logs.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              ບັນທຶກການເຄື່ອນໄຫວ
            </h1>
            <p className="mt-2 text-gray-600 text-sm sm:text-base">
              ຕິດຕາມແລະຕິດຕາມການເຄື່ອນໄຫວທັງໝົດຂອງລະບົບ
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <div className="text-sm text-gray-500 flex items-center">
              <span className="inline-flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                <span className="whitespace-nowrap">
                  ອັບເດດຫຼ້າສຸດ: {lastUpdate.toLocaleTimeString("lo-LA")}
                </span>
              </span>
            </div>
            <button
              onClick={() => {
                console.log("Manual refresh clicked");
                fetchLogs();
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto"
              title="ລື່ນໃໝ່"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              ລື່ນໃໝ່
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <form
          onSubmit={handleSearch}
          className="flex flex-wrap items-center gap-3"
        >
          {/* Action Filter */}
          <div className="relative w-56">
            <label htmlFor="actionFilter" className="sr-only">
              ກັ່ນຕອງຕາມການກະທຳ
            </label>
            <select
              id="actionFilter"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="w-full pl-9 pr-10 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white truncate"
              aria-label="ກັ່ນຕອງຕາມການກະທຳ"
            >
              <option value="">ການກະທຳທັງໝົດ</option>
              {actions.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
            <FunnelIcon className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
            <ChevronDownIcon className="absolute right-3 top-2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Search Input */}
          <div className="relative flex-1 min-w-[200px]">
            <label htmlFor="searchTerm" className="sr-only">
              ຄົ້ນຫາການເຄື່ອນໄຫວ
            </label>
            <input
              id="searchTerm"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ຄົ້ນຫາການເຄື່ອນໄຫວ..."
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <div className="relative w-36">
              <label htmlFor="startDate" className="sr-only">
                ວັນທີເລີ່ມຕົ້ນ
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-9 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                aria-label="ວັນທີເລີ່ມຕົ້ນ"
              />
              <CalendarDaysIcon className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
            </div>

            <span className="text-gray-400 text-sm">-</span>

            <div className="relative w-36">
              <label htmlFor="endDate" className="sr-only">
                ວັນທີສິ້ນສຸດ
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-9 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                aria-label="ວັນທີສິ້ນສຸດ"
              />
              <CalendarDaysIcon className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-auto sm:ml-0"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            ນຳໃຊ້
          </button>
        </form>
      </div>

      {/* Activity Logs Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ຜູ້ໃຊ້
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ການກະທຳ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ລາຍລະອຽດ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ເວລາ
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    ບໍ່ພົບບັນທຶກການເຄື່ອນໄຫວ
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  console.log("Rendering log entry:", {
                    id: log.id,
                    user_id: log.user_id,
                    user_email: log.user_email,
                  });
                  const actionInfo = getActionInfo(log.action, log.details);
                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            {loadingUsers.has(log.user_id) ? (
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                              </div>
                            ) : userProfiles[log.user_id]?.profile_image ? (
                              <img
                                src={getImageUrl(
                                  userProfiles[log.user_id].profile_image || "",
                                )}
                                alt="Profile"
                                className="h-8 w-8 rounded-full object-cover border border-gray-200"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.nextElementSibling?.classList.remove(
                                    "hidden",
                                  );
                                }}
                              />
                            ) : null}
                            <div
                              className={`h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center ${userProfiles[log.user_id]?.profile_image ? "hidden" : ""}`}
                            >
                              <UserCircleIcon className="h-5 w-5 text-gray-500" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <button
                              onClick={() => {
                                console.log("=== BUTTON CLICKED ===");
                                console.log(
                                  "Button clicked for user:",
                                  log.user_id,
                                  log.user_email,
                                );
                                if (userProfiles[log.user_id]) {
                                  setSelectedUser(userProfiles[log.user_id]);
                                  setShowUserModal(true);
                                } else {
                                  handleUserClick(log.user_id);
                                }
                              }}
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors cursor-pointer underline"
                              title="ຄລິກເພື່ອເບິ່ງຂໍ້ມູນຜູ້ໃຊ້"
                            >
                              {userProfiles[log.user_id]?.name ||
                                log.user_email}
                            </button>
                            <div className="text-sm text-gray-500">
                              {userProfiles[log.user_id] ? (
                                <>
                                  <span className="text-gray-400">
                                    {log.user_email}
                                  </span>
                                  <span className="mx-1">•</span>
                                  <span className="font-mono">
                                    {userProfiles[log.user_id].student_id ||
                                      `STD-${String(log.user_id).padStart(5, "0")}`}
                                  </span>
                                </>
                              ) : (
                                <span className="font-mono">
                                  STD-{String(log.user_id).padStart(5, "0")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          <div>{getActionBadge(log.action)}</div>
                          <div className="flex items-center space-x-2">
                            {getMethodBadge(actionInfo.method)}
                            <span className="text-sm font-mono text-gray-600">
                              {actionInfo.endpoint}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {actionInfo.description}
                          </div>
                          {log.details &&
                            log.details !== actionInfo.description && (
                              <div className="text-gray-600 mt-1">
                                {log.details}
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {formatDateTime(log.created_at)}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-8 flex justify-end mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center justify-center px-4 py-2 rounded-md ${
              currentPage === 1
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            }`}
            aria-label="ໜ້າກ່ອນໜ້າ"
            title="ໜ້າກ່ອນໜ້າ"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>

          {/* Current page indicator */}
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-blue-600 text-white font-medium">
            {currentPage}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={
              currentPage === pagination.totalPages ||
              pagination.totalPages === 0
            }
            className={`flex items-center justify-center px-4 py-2 rounded-md ${
              currentPage === pagination.totalPages ||
              pagination.totalPages === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            }`}
            aria-label="ໜ້າຕໍ່ໄປ"
            title="ໜ້າຕໍ່ໄປ"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* User Profile Modal */}
      <UserProfileModal
        user={selectedUser}
        isOpen={showUserModal}
        onClose={closeUserModal}
      />
    </div>
  );
};

export default ActivityLogs;
